import React, { useMemo, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { BsChevronDown, BsChevronRight, BsFolder } from 'react-icons/bs';
import FileTypeChip from 'DataSources/file/FileTypeChip';
import { flattenRows, GroupAccordion } from './InventoryFieldsAccordion';
import {
  groupFilesByFolder,
  fileSchemeSupport,
} from '../fileInventoryUtils';

// Inventory-key extension → the FileNodeType name FileTypeChip colors by.
const EXT_TO_NODE_TYPE = {
  csv: 'CSV', tsv: 'TSV', parquet: 'PARQUET', json: 'JSON', jsonl: 'JSON',
  ndjson: 'JSON', xml: 'XML', xlsx: 'EXCEL', xls: 'EXCEL', xlsm: 'EXCEL',
  pdf: 'PDF', docx: 'DOCX',
};

/**
 * File-source masking inventory body: the stored inventory's entries (table = file)
 * grouped under their folder path. Each folder header carries a [Mask folder] action;
 * each file renders as a GroupAccordion with a per-file [Mask] in the header. Both
 * fire ONE masking job with an inline-inventory subset via `onMaskFiles(tableKeys,
 * label)` — the host (InventoryPage) persists the full inventory first and starts
 * the job.
 *
 * Field editing inside each file reuses the accordion's row components unchanged.
 */
function FileInventoryGroups({
  selectedFields,
  setSelectedFields,
  algorithms = [],
  readOnly = false,
  source,
  tableMeta,
  submitting = false,
  onMaskFiles,
}) {
  const [collapsedFolders, setCollapsedFolders] = useState({});
  const [collapsedFiles, setCollapsedFiles] = useState({});

  const scheme = useMemo(() => fileSchemeSupport(source), [source]);
  const groups = useMemo(
    () =>
      groupFilesByFolder(selectedFields, source && source.rootPath, source && source.datasourceType, tableMeta),
    [selectedFields, source, tableMeta]
  );

  // --- writers (same shape as InventoryFieldsAccordion's) --------------------------
  const writeField = (tableKey, field, patch) => {
    setSelectedFields((prev) => {
      const t = prev[tableKey];
      if (!t || !t[field]) return prev;
      return { ...prev, [tableKey]: { ...t, [field]: { ...t[field], ...patch } } };
    });
  };

  const writeRows = (rows, compute) => {
    setSelectedFields((prev) => {
      const next = { ...prev };
      rows.forEach((r) => {
        const t = next[r.tableKey];
        if (!t || !t[r.field]) return;
        const patch = compute(r);
        if (!patch) return;
        next[r.tableKey] = { ...next[r.tableKey], [r.field]: { ...next[r.tableKey][r.field], ...patch } };
      });
      return next;
    });
  };

  const toggleGroupMask = (rows) => {
    if (readOnly) return;
    const allOn = rows.length > 0 && rows.every((r) => r.checked);
    writeRows(rows, () => ({ checked: !allOn }));
  };

  const setAllInGroup = (rows, value) => {
    if (readOnly || !value) return;
    if (value === '__REC__') {
      writeRows(rows, (r) => ({
        algorithm: r.recommendedAlgorithm || '',
        params: r.recommendedParams || {},
      }));
    } else {
      writeRows(rows, () => ({ algorithm: value }));
    }
  };

  // --- mask action gating ----------------------------------------------------------
  const maskDisabledReason = (selectedCount, maskable) => {
    if (!scheme.enabled) return scheme.reason;
    if (maskable === false) return 'This file type is not supported by masking.';
    if (selectedCount === 0) return 'No fields selected for masking.';
    return null;
  };

  const maskButton = (label, tableKeys, selectedCount, maskable, testId) => {
    const reason = maskDisabledReason(selectedCount, maskable);
    const disabled = readOnly || submitting || !!reason;
    return (
      <Button
        size='sm'
        variant='primary'
        data-testid={testId}
        className='mr-2'
        disabled={disabled}
        title={reason || undefined}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) onMaskFiles(tableKeys, label);
        }}
        style={{ whiteSpace: 'nowrap', padding: '0 10px', fontSize: 12 }}
      >
        Mask
      </Button>
    );
  };

  if (groups.length === 0) {
    return <div className='inv-empty'>No files in this inventory.</div>;
  }

  return (
    <div className='inventory-accordion'>
      {!scheme.enabled && (
        <Alert variant='warning' className='mb-3' data-testid='file-scheme-unsupported'>
          {scheme.reason}
        </Alert>
      )}
      {groups.map((g) => {
        const folderOpen = collapsedFolders[g.folder] !== true;
        const maskableFiles = g.files.filter((f) => f.maskable);
        const folderKeys = maskableFiles.map((f) => f.tableKey);
        const folderSelected = maskableFiles.reduce((s, f) => s + f.selectedCount, 0);
        return (
          <div key={g.folder} className='mb-3'>
            {/* Folder header */}
            <div
              className='inv-head'
              style={{ borderRadius: 6 }}
              onClick={() =>
                setCollapsedFolders((prev) => ({ ...prev, [g.folder]: folderOpen }))
              }
            >
              {folderOpen ? <BsChevronDown className='inv-chevron' /> : <BsChevronRight className='inv-chevron' />}
              <BsFolder className='mr-2' />
              <div className='flex-grow-1 text-truncate' style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                {g.folder}
              </div>
              <span className='inv-mini mr-2'>
                {g.files.length} {g.files.length === 1 ? 'file' : 'files'} · {folderSelected} fields selected
              </span>
              {maskButton(
                `folder ${g.folder}`,
                folderKeys,
                folderSelected,
                maskableFiles.length > 0,
                'mask-folder-btn'
              )}
            </div>

            {/* Per-file accordions */}
            {folderOpen && g.files.map((f) => {
              const rows = flattenRows({ [f.tableKey]: selectedFields[f.tableKey] });
              const fileOpen = collapsedFiles[f.tableKey] !== true;
              return (
                <div
                  key={f.tableKey}
                  style={{ marginLeft: 18, marginTop: 8, opacity: f.maskable ? 1 : 0.55 }}
                >
                  <GroupAccordion
                    bucketKey={f.tableKey}
                    list={rows}
                    open={fileOpen}
                    groupBy='file'
                    isEncryption={false}
                    algorithms={algorithms}
                    readOnly={readOnly || !f.maskable}
                    searching={false}
                    showAll
                    onShowAll={() => {}}
                    onToggleCollapsed={() =>
                      setCollapsedFiles((prev) => ({ ...prev, [f.tableKey]: fileOpen }))
                    }
                    onToggleGroupMask={toggleGroupMask}
                    onSetAllInGroup={setAllInGroup}
                    onToggleRow={(r) => writeField(r.tableKey, r.field, { checked: !r.checked })}
                    onAlgoRow={(r, v) => writeField(r.tableKey, r.field, { algorithm: v })}
                    onApplyRow={(r) =>
                      writeField(r.tableKey, r.field, {
                        algorithm: r.recommendedAlgorithm || '',
                        params: r.recommendedParams || {},
                      })
                    }
                    headerExtra={(
                      <span className='d-inline-flex align-items-center mr-2' style={{ gap: 6 }}>
                        {/* Extension chip when the name carries one; otherwise the content-sniffed
                            format (FileTypeChip renders DatasourceType names as human labels). */}
                        {f.ext ? (
                          <FileTypeChip type={EXT_TO_NODE_TYPE[f.ext] || f.ext.toUpperCase()} />
                        ) : f.sniffedType ? (
                          <FileTypeChip type={f.sniffedType} />
                        ) : null}
                        {!f.maskable && (
                          <span className='inv-mini' style={{ color: '#B45309' }}>
                            not supported by masking
                          </span>
                        )}
                        {f.maskable && maskButton(
                          f.baseName,
                          [f.tableKey],
                          f.selectedCount,
                          true,
                          'mask-file-btn'
                        )}
                      </span>
                    )}
                  />
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

export default FileInventoryGroups;
